import { FC, useMemo } from "react"
import { BBColumnDef, Integration, IntegrationStatus } from "../../../../types"
import If from "../../if"
import Table from "../../table"
import { formatDateTimeFromTimestamp } from "../../../utils"
import { useGetEmbedStatsOrgQuery } from "../../../../redux/queries"
import { useAppSelector } from "../../../hooks"
import { selectUserDetails } from "../../../../redux/slices/auth"
import Skeleton from "../../skeletons/base"

interface Props {
  integration?: Integration
  statusData?: IntegrationStatus[]
  isLoading: boolean
}

const Status: FC<Props> = ({ integration, statusData = [], isLoading }) => {
  const userDetails = useAppSelector(selectUserDetails)
  const orgId = userDetails?.org?.id

  // Fetch org stats for different ranges
  const { data: statsData7d = [], isLoading: isLoading7d } =
    useGetEmbedStatsOrgQuery(
      { org_id: Number(orgId!), range: 7 },
      { skip: !orgId || !integration?.id }
    )

  const { data: statsData30d = [], isLoading: isLoading30d } =
    useGetEmbedStatsOrgQuery(
      { org_id: Number(orgId!), range: 30 },
      { skip: !orgId || !integration?.id }
    )

  const { data: statsData90d = [], isLoading: isLoading90d } =
    useGetEmbedStatsOrgQuery(
      { org_id: Number(orgId!), range: 90 },
      { skip: !orgId || !integration?.id }
    )

  const isLoadingPageviews = isLoading7d || isLoading30d || isLoading90d

  // Extract pageviews for this specific integration
  const pageviews7d = useMemo(() => {
    const row = statsData7d.find(r => String(r.id) === integration?.id)
    if (!row) return 0
    const requests =
      typeof row.requests === "string"
        ? parseInt(row.requests, 10)
        : row.requests
    return isNaN(requests) ? 0 : requests
  }, [statsData7d, integration?.id])

  const pageviews30d = useMemo(() => {
    const row = statsData30d.find(r => String(r.id) === integration?.id)
    if (!row) return 0
    const requests =
      typeof row.requests === "string"
        ? parseInt(row.requests, 10)
        : row.requests
    return isNaN(requests) ? 0 : requests
  }, [statsData30d, integration?.id])

  const pageviews90d = useMemo(() => {
    const row = statsData90d.find(r => String(r.id) === integration?.id)
    if (!row) return 0
    const requests =
      typeof row.requests === "string"
        ? parseInt(row.requests, 10)
        : row.requests
    return isNaN(requests) ? 0 : requests
  }, [statsData90d, integration?.id])

  const columns: BBColumnDef<IntegrationStatus>[] = [
    {
      id: "timestamp",
      header: "Date/Time",
      accessorFn: row => row.timestamp,
      footer: props => props.column.id,
      cell: props => (
        <td
          className={`px-2 py-4 ${
            props.row.original.validated === "1" ? "text-primary-400" : ""
          }`}
        >
          {formatDateTimeFromTimestamp(
            props.getValue() as string,
            "LL/dd/yyyy h:mm:ss a"
          )}
        </td>
      )
    },
    {
      id: "referrer",
      header: "Referrer",
      accessorFn: row => row.referrer,
      footer: props => props.column.id,
      cell: props => (
        <td
          className={`pl-3 py-4 ${
            props.row.original.validated === "1" ? "text-primary-400" : ""
          }`}
          colSpan={2}
        >
          {props.getValue() as string}
        </td>
      ),
      meta: { headerClassName: "hidden md:table-cell" }
    }
  ]

  return (
    <div>
      {/* PAGE LOADS Stats */}
      <div className="mt-6 px-2">
        <div className="text-neutral-400 uppercase text-sm font-medium mb-3">
          PAGE LOADS
        </div>
        {isLoadingPageviews ? (
          <Skeleton>
            <div className="flex items-center gap-8">
              <div className="h-5 w-32 bg-gray-200 rounded-full" />
              <div className="h-5 w-36 bg-gray-200 rounded-full" />
              <div className="h-5 w-36 bg-gray-200 rounded-full" />
            </div>
          </Skeleton>
        ) : (
          <div className="flex items-center gap-8">
            <div>Last 7 days: {pageviews7d.toLocaleString()}</div>
            <div>Last 30 days: {pageviews30d.toLocaleString()}</div>
            <div>Last 90 days: {pageviews90d.toLocaleString()}</div>
          </div>
        )}
      </div>

      {/* Status Table */}
      <If
        isLoading={isLoading}
        condition={statusData?.length > 0}
        then={
          <Table
            className="mt-8"
            columns={columns}
            data={statusData}
            viewType="table"
            pageSize={25}
            searchBarVisible={false}
          />
        }
        else={<div className="px-4 py-6">No data available.</div>}
      />
    </div>
  )
}

export default Status
